import React, { useState } from 'react';
import { BookOpen, Copy, Download, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import { copyToClipboard } from '../utils/share';
import { downloadVerseCard } from '../utils/verseCardImage';
import { getVerseResultUrl } from '../utils/verseResultLink';
import { trackEvent } from '../utils/analytics';
import { BirthVerse } from '../types/verses';
import ShareButton from './ShareButton';

interface VerseCardProps {
  verse: BirthVerse;
  date: Date;
  entryPoint?: 'homepage' | 'calculator' | 'monthly_page';
  onFindFriend?: () => void;
}

const VerseCard: React.FC<VerseCardProps> = ({
  verse,
  date,
  entryPoint = 'calculator',
  onFindFriend
}) => {
  const [copyPending, setCopyPending] = useState(false);
  const [downloadPending, setDownloadPending] = useState(false);
  const [notice, setNotice] = useState<{ url: string; status?: string; error?: string }>();
  const formattedDate = date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
  const resultUrl = getVerseResultUrl(date);
  const status = notice?.url === resultUrl ? notice.status : '';
  const error = notice?.url === resultUrl ? notice.error : '';
  const shareText = `My BirthVerse (${formattedDate}): "${verse.text}" — ${verse.reference}\n\nFind your BirthVerse: ${resultUrl}`;
  const properties = { calculator: 'birthverse', entry_point: entryPoint } as const;

  const handleCopyClick = async () => {
    setCopyPending(true);
    setNotice(undefined);
    try {
      await copyToClipboard(shareText);
      setNotice({ url: resultUrl, status: 'Verse and result link copied.' });
      trackEvent('verse_copy', { ...properties, method: 'clipboard' });
    } catch {
      setNotice({ url: resultUrl, error: 'Could not copy the verse. Try again, use Share, or download your card.' });
    } finally {
      setCopyPending(false);
    }
  };

  const handleDownload = async () => {
    setDownloadPending(true);
    setNotice(undefined);
    try {
      await downloadVerseCard(verse, date);
      setNotice({ url: resultUrl, status: 'Verse card download started. Check your downloads to save it.' });
      trackEvent('verse_download', { ...properties, method: 'png' });
    } catch {
      setNotice({ url: resultUrl, error: 'Could not create your card. Try again, or copy the verse instead.' });
    } finally {
      setDownloadPending(false);
    }
  };

  const handleFindFriend = () => {
    trackEvent('friend_lookup', properties);
    onFindFriend?.();
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm">
      <div className="p-4 sm:p-8">
        <div className="flex items-start gap-3 mb-4 sm:mb-6">
          <div className="bg-[#F5F3FF] p-2 sm:p-2.5 rounded-xl shrink-0">
            <BookOpen className="h-5 w-5 sm:h-6 sm:w-6 text-[#4F46E5]" aria-hidden="true" />
          </div>
          <h3 className="text-lg sm:text-xl font-semibold text-gray-900">
            Your BirthVerse for {formattedDate}
          </h3>
        </div>

        <blockquote className="text-lg sm:text-xl text-gray-700 mb-4 sm:mb-6 border-l-2 border-[#DDD6FE] pl-4 sm:pl-6">
          {verse.text}
        </blockquote>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between text-sm gap-2">
          <cite className="text-[#4F46E5] font-semibold not-italic text-base sm:text-lg">
            {verse.reference}
          </cite>
          <span className="text-gray-500">Chapter {verse.chapter}, Verse {verse.verse}</span>
        </div>

        <div className="flex flex-wrap items-center gap-2 mt-6 pt-5 border-t border-gray-100">
          <button
            type="button"
            onClick={handleDownload}
            disabled={downloadPending}
            className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-4 py-3 bg-[#4F46E5] text-white font-medium rounded-lg hover:bg-[#4338CA] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#4F46E5] disabled:opacity-60"
          >
            <Download className="h-4 w-4" aria-hidden="true" />
            {downloadPending ? 'Creating your card…' : 'Download verse card'}
          </button>
          <button
            type="button"
            onClick={handleCopyClick}
            disabled={copyPending}
            className="inline-flex items-center gap-2 px-3 py-2 text-gray-600 rounded-lg hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#4F46E5] disabled:opacity-60"
          >
            <Copy className="h-4 w-4" aria-hidden="true" />
            {copyPending ? 'Copying…' : 'Copy verse'}
          </button>
          <ShareButton
            key={resultUrl}
            title="My BirthVerse"
            text={shareText}
            url={resultUrl}
            onShare={method => trackEvent('verse_share', { ...properties, method })}
          />
        </div>

        <div className="mt-4 text-sm">
          {onFindFriend ? (
            <button type="button" onClick={handleFindFriend} className="inline-flex items-center gap-2 text-[#4F46E5] font-medium hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#4F46E5]">
              <Users className="h-4 w-4" aria-hidden="true" />Find a friend's verse
            </button>
          ) : (
            <Link to="/calculators/birthverse" onClick={handleFindFriend} className="inline-flex items-center gap-2 text-[#4F46E5] font-medium hover:underline">
              <Users className="h-4 w-4" aria-hidden="true" />Find a friend's verse
            </Link>
          )}
        </div>
        {status && <p role="status" className="mt-3 text-sm text-gray-600">{status}</p>}
        {error && <p role="alert" className="mt-3 text-sm text-red-700">{error}</p>}
      </div>
    </div>
  );
};

export default VerseCard;
